/*
 * Custom multipack selector for Shopify Themes.
 * Configure offers with product tags such as `bundle:1:30:1` and `bundle:2:36:2`.
 * The offer price is presentational; Shopify automatic discounts enforce checkout pricing.
 */
class PackageBuilder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    const configElement = this.querySelector('[data-package-builder-config]');
    if (!configElement) return;

    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.warn('Package builder configuration could not be read.', error);
      return;
    }

    this.offers = this.getOffers(this.config.tags || []);
    if (!this.offers.length) return;

    this.labels = this.getLabels();
    this.product = this.config.product;
    this.productInfo = this.closest('product-info');
    this.variantSelects = this.productInfo?.querySelector('variant-selects');
    this.form = this.productInfo?.querySelector('form[data-type="add-to-cart-form"]');
    if (!this.form || !this.product?.variants?.length) return;

    this.initialized = true;
    this.nativeInputs = [...(this.variantSelects?.querySelectorAll('.product-form__input') || [])];
    this.activeOffer = this.offers[0];
    this.render();
    this.syncNativePickers();
    // Theme editor and product updates can replace the native picker after initialization.
    this.pickerObserver = new MutationObserver(() => this.syncNativePickers());
    this.pickerObserver.observe(this.productInfo, { childList: true, subtree: true });
    this.addEventListener('click', this.onClick.bind(this));
    this.submitHandler = this.onSubmit.bind(this);
    this.form.addEventListener('submit', this.submitHandler, true);
  }

  disconnectedCallback() {
    this.pickerObserver?.disconnect();
    this.nativePickerStates?.forEach((state, picker) => {
      picker.hidden = state.hidden;
      picker.inert = state.inert;
      if (state.display) picker.style.setProperty('display', state.display, state.priority);
      else picker.style.removeProperty('display');
    });
    this.nativePickerStates?.clear();
    this.form?.removeEventListener('submit', this.submitHandler, true);
    this.productInfo?.classList.remove('package-builder-enabled', 'package-builder-multipack');
    this.initialized = false;
  }

  getOffers(tags) {
    return tags
      .map((tag) => {
        const match = String(tag).trim().match(/^bundle:(\d+):(\d+(?:\.\d+)?):(\d+)$/i);
        if (!match) return null;
        return {
          quantity: Number(match[1]),
          price: Number(match[2]),
          selectionCount: Number(match[3]),
        };
      })
      .filter(Boolean)
      .filter((offer) => Number.isSafeInteger(offer.quantity) && offer.quantity > 0 && offer.quantity <= 100 && Number.isSafeInteger(offer.selectionCount) && offer.selectionCount > 0 && offer.selectionCount <= 100 && Number.isFinite(offer.price) && offer.price >= 0)
      .sort((a, b) => a.quantity - b.quantity)
      .filter((offer, index, offers) => offers.findIndex((item) => item.quantity === offer.quantity) === index);
  }

  syncNativePickers() {
    if (!this.offers?.length || !this.productInfo) return;
    this.nativePickerStates ||= new Map();
    // Globo injects its own picker beside Dawn's hidden variant-selects.
    this.productInfo.querySelectorAll('variant-selects, .globo-swatch-product-detail').forEach((picker) => {
      if (this.contains(picker)) return;
      if (!this.nativePickerStates.has(picker)) {
        this.nativePickerStates.set(picker, {
          hidden: picker.hidden,
          inert: picker.inert,
          display: picker.style.getPropertyValue('display'),
          priority: picker.style.getPropertyPriority('display'),
        });
      }
      picker.hidden = true;
      picker.inert = true;
      picker.style.setProperty('display', 'none', 'important');
    });
  }

  getLabels() {
    const language = String(this.config.locale || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    const translations = {
      ro: { package: 'Pachet', item: 'Articol', unit: 'BUC.', packageItem: 'Articol pachet', chooseAvailable: 'Alegeți o opțiune disponibilă pentru fiecare articol.' },
      bg: { package: 'Пакет', item: 'Артикул', unit: 'БР.', packageItem: 'Артикул от пакета', chooseAvailable: 'Изберете налична опция за всеки артикул.' },
      es: { package: 'Paquete', item: 'Artículo', unit: 'UDS.', packageItem: 'Artículo del paquete', chooseAvailable: 'Elija una opción disponible para cada artículo.' },
      el: { package: 'Πακέτο', item: 'Προϊόν', unit: 'ΤΕΜ.', packageItem: 'Προϊόν πακέτου', chooseAvailable: 'Επιλέξτε μια διαθέσιμη επιλογή για κάθε προϊόν.' },
      fr: { package: 'Lot', item: 'Article', unit: 'PCS', packageItem: 'Article du lot', chooseAvailable: 'Choisissez une option disponible pour chaque article.' },
      de: { package: 'Paket', item: 'Artikel', unit: 'STK.', packageItem: 'Paketartikel', chooseAvailable: 'Wählen Sie für jeden Artikel eine verfügbare Option.' },
      it: { package: 'Pacchetto', item: 'Articolo', unit: 'PZ.', packageItem: 'Articolo del pacchetto', chooseAvailable: 'Scegli un’opzione disponibile per ogni articolo.' },
      pt: { package: 'Pacote', item: 'Artigo', unit: 'UN.', packageItem: 'Artigo do pacote', chooseAvailable: 'Escolha uma opção disponível para cada artigo.' },
      nl: { package: 'Pakket', item: 'Artikel', unit: 'ST.', packageItem: 'Pakketartikel', chooseAvailable: 'Kies voor elk artikel een beschikbare optie.' },
      pl: { package: 'Pakiet', item: 'Produkt', unit: 'SZT.', packageItem: 'Produkt w pakiecie', chooseAvailable: 'Wybierz dostępną opcję dla każdego produktu.' },
      hu: { package: 'Csomag', item: 'Tétel', unit: 'DB', packageItem: 'Csomag tétel', chooseAvailable: 'Válasszon elérhető opciót minden tételhez.' },
      hr: { package: 'Paket', item: 'Artikl', unit: 'KOM.', packageItem: 'Artikl paketa', chooseAvailable: 'Odaberite dostupnu opciju za svaki artikl.' },
      cs: { package: 'Balíček', item: 'Položka', unit: 'KS', packageItem: 'Položka balíčku', chooseAvailable: 'Vyberte dostupnou možnost pro každou položku.' },
      sk: { package: 'Balíček', item: 'Položka', unit: 'KS', packageItem: 'Položka balíčka', chooseAvailable: 'Vyberte dostupnú možnosť pre každú položku.' },
      lt: { package: 'Rinkinys', item: 'Prekė', unit: 'VNT.', packageItem: 'Rinkinio prekė', chooseAvailable: 'Pasirinkite galimą variantą kiekvienai prekei.' },
      et: { package: 'Komplekt', item: 'Toode', unit: 'TK', packageItem: 'Komplekti toode', chooseAvailable: 'Valige iga toote jaoks saadaolev valik.' },
      lv: { package: 'Komplekts', item: 'Prece', unit: 'GAB.', packageItem: 'Komplekta prece', chooseAvailable: 'Izvēlieties pieejamu opciju katrai precei.' },
      tr: { package: 'Paket', item: 'Ürün', unit: 'ADET', packageItem: 'Paket ürünü', chooseAvailable: 'Her ürün için uygun bir seçenek belirleyin.' },
      zh: { package: '套餐', item: '商品', unit: '件', packageItem: '套餐商品', chooseAvailable: '请为每件商品选择可售规格。' },
      ja: { package: 'セット', item: '商品', unit: '点', packageItem: 'セット商品', chooseAvailable: '各商品で在庫のあるオプションを選択してください。' },
      ko: { package: '세트', item: '상품', unit: '개', packageItem: '세트 상품', chooseAvailable: '각 상품의 판매 가능한 옵션을 선택하세요.' },
    };
    return translations[language] || { package: 'Package', item: 'Item', unit: 'PCS', packageItem: 'Package item', chooseAvailable: 'Please choose an available option for every item.' };
  }

  money(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.config.currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (_) {
      return `${value} ${this.config.currency || ''}`.trim();
    }
  }

  offerLabel(offer) {
    return `${offer.quantity} ${this.labels.unit}`;
  }

  get buyNowLabel() {
    const language = String(this.config.locale || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    return ({ hr: 'Kupi odmah', bg: 'Купете сега', ro: 'Cumpără acum', pl: 'Kup teraz',
      es: 'Comprar ahora', sk: 'Kúpiť teraz', cs: 'Koupit nyní', de: 'Jetzt kaufen',
      fr: 'Acheter maintenant', it: 'Acquista ora', pt: 'Comprar agora', nl: 'Nu kopen',
      el: 'Αγορά τώρα', hu: 'Vásárlás most', lt: 'Pirkti dabar', et: 'Osta kohe',
      lv: 'Pirkt tagad', tr: 'Hemen satın al', zh: '立即购买', ja: '今すぐ購入', ko: '지금 구매' })[language] || 'Buy it now';
  }

  get startingVariant() {
    return this.product.variants.find((variant) => variant.available) || this.product.variants[0];
  }

  render() {
    this.hidden = false;
    this.innerHTML = `
      <fieldset class="package-picker" aria-label="${this.labels.package}">
        <legend class="form__label">${this.labels.package}</legend>
        <div class="package-picker__offers">
          ${this.offers
            .map(
              (offer) => `
                <button type="button" class="package-card ${offer.quantity === this.activeOffer.quantity ? 'is-selected' : ''}" data-package-quantity="${offer.quantity}" aria-pressed="${offer.quantity === this.activeOffer.quantity}">
                  <span class="package-card__radio" aria-hidden="true"></span>
                  <span class="package-card__content"><span class="package-card__title">${this.offerLabel(offer)}</span></span>
                  <span class="package-card__price">${this.money(offer.price)}</span>
                </button>
              `
            )
            .join('')}
        </div>
      </fieldset>
      <div class="package-builder__components" ></div>
      <button type="button" class="package-builder__buy-now button button--full-width button--primary" data-package-buy-now>
        ${this.buyNowLabel}
      </button>
    `;
    this.setMode();
  }

  setMode() {
    const isMultipack = true; // Every offer renders its configured number of independent selectors.
    this.productInfo.classList.add('package-builder-enabled');
    this.productInfo.classList.toggle('package-builder-multipack', isMultipack);
    const quantity = this.productInfo.querySelector('input[name=quantity]');
    if (quantity) quantity.value = '1';
    this.form.closest('product-form')?.classList.toggle('package-builder-active', isMultipack);
    if (isMultipack) this.renderComponents();
  }

  renderComponents() {
    const components = this.querySelector('.package-builder__components');
    const variant = this.startingVariant;
    const values = variant.options || [];
    if (this.config.hasOnlyDefaultVariant) { components.innerHTML = ''; return; }
    components.innerHTML = Array.from({ length: this.activeOffer.selectionCount }, (_, componentIndex) => `
      <fieldset class="package-builder__component" data-component-index="${componentIndex}">
        <legend class="form__label">${this.labels.item} ${componentIndex + 1}</legend>
        ${this.product.options
          .map(
            (optionName, optionIndex) => `
              <div class="package-builder__option" data-option-index="${optionIndex}">
                <span class="package-builder__option-label">${this.escape(optionName)}: <span data-package-selected-value>${this.escape(values[optionIndex] || '')}</span></span>
                <div class="package-builder__values">
                  ${[...new Set(this.product.variants.map((item) => item.options[optionIndex]))]
                    .map(
                      (value) => {
                        const thumbnail = this.optionThumbnail(optionName, optionIndex, value, values);
                        const selected = value === values[optionIndex];
                        return `<button type="button" class="package-builder__value ${thumbnail ? 'package-builder__value--image' : ''} ${selected ? 'is-selected' : ''}" data-value="${this.escape(value)}" aria-label="${this.escape(value)}" aria-pressed="${selected}" title="${this.escape(value)}">${thumbnail ? `<img src="${this.escape(thumbnail)}" alt="${this.escape(value)}" width="80" height="80" loading="lazy"><span class="visually-hidden">${this.escape(value)}</span>` : this.escape(value)}</button>`;
                      }
                    )
                    .join('')}
                </div>
              </div>
            `
          )
          .join('')}
      </fieldset>
    `).join('');
  }

  optionThumbnail(optionName, optionIndex, value, selections) {
    const swatches = this.config.optionSwatches?.[optionIndex] || [];
    const swatchImage = swatches.find((swatch) => swatch.value === value)?.image;
    if (swatchImage) return swatchImage;
    const colorNames = /^(colou?r|colou?rs|couleurs?|farbe|farben|colore|colori|cor|cores|kleur|kleuren|kolor|kolory|barva|barvy|farba|farby|boja|boje|culoare|culori|цвят|цвет|χρώμα|szín|renk|spalva|värv|krāsa|颜色|顏色|色|색상)$/iu;
    if (!colorNames.test(String(optionName).trim()) && !swatches.some((swatch) => swatch.image)) return null;
    const matching = this.product.variants.filter((variant) => variant.options[optionIndex] === value);
    const exact = matching.find((variant) => variant.options.every((item, index) => index === optionIndex || item === selections[index]));
    const imageFor = (variant) => variant && (this.config.variantImages?.[variant.id] || variant.featured_image?.src);
    return imageFor(exact) || matching.map(imageFor).find(Boolean) || null;
  }

  escape(value) {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  onClick(event) {
    if (this.submitting) return;
    if (event.target.closest('[data-package-buy-now]')) {
      event.preventDefault();
      this.addPackage(true);
      return;
    }
    const offerButton = event.target.closest('[data-package-quantity]');
    if (offerButton) {
      this.activeOffer = this.offers.find((offer) => offer.quantity === Number(offerButton.dataset.packageQuantity));
      this.render();
      return;
    }

    const valueButton = event.target.closest('.package-builder__value');
    if (valueButton) {
      valueButton.parentElement.querySelectorAll('.package-builder__value').forEach((button) => {
        button.classList.remove('is-selected');
        button.setAttribute('aria-pressed', 'false');
      });
      valueButton.classList.add('is-selected');
      valueButton.setAttribute('aria-pressed', 'true');
      const selectedLabel = valueButton.closest('.package-builder__option')?.querySelector('[data-package-selected-value]');
      if (selectedLabel) selectedLabel.textContent = valueButton.dataset.value;
    }
  }

  selectedVariants() {
    if (this.config.hasOnlyDefaultVariant) return Array(this.activeOffer.selectionCount).fill(this.product.variants.find((variant) => variant.available));
    return [...this.querySelectorAll('.package-builder__component')].map((component) => {
      const selections = [...component.querySelectorAll('.package-builder__option')].map((option) => option.querySelector('.package-builder__value.is-selected')?.dataset.value);
      return this.product.variants.find((variant) => variant.available && variant.options.every((value, index) => value === selections[index]));
    });
  }

  onSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addPackage(false);
  }

  async addPackage(goToCheckout) {
    if (this.submitting) return;
    const variants = this.selectedVariants();
    if (variants.length !== this.activeOffer.selectionCount || variants.some((variant) => !variant)) {
      window.alert(this.labels.chooseAvailable);
      return;
    }

    this.submitting = true;
    this.setAttribute('aria-busy', 'true');
    const buyButton = this.querySelector('[data-package-buy-now]');
    if (buyButton) {
      buyButton.disabled = true;
      buyButton.setAttribute('aria-busy', 'true');
    }
    const bundleId = `bundle-${Date.now()}`;
    const items = variants.map((variant, index) => ({
      id: variant.id,
      quantity: 1,
      properties: {
        [this.labels.package]: this.offerLabel(this.activeOffer),
        [this.labels.packageItem]: `${index + 1} / ${this.activeOffer.selectionCount}`,
        '_package_id': bundleId,
      },
    }));

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || 'Package could not be added.');
      const storefrontRoot = window.Shopify?.routes?.root || '/';
      window.location.href = goToCheckout ? `${storefrontRoot.replace(/\/?$/, '/')}checkout` : window.routes.cart_url;
    } catch (error) {
      console.error(error);
      window.alert(error.message || 'Package could not be added.');
    } finally {
      this.submitting = false;
      this.removeAttribute('aria-busy');
      if (buyButton) {
        buyButton.disabled = false;
        buyButton.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('package-builder')) customElements.define('package-builder', PackageBuilder);
